import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Plus, Minus } from "lucide-react";
import { formatPrice } from "@/lib/format";
import { mediaUrl } from "@/lib/api";
import { PHOTO } from "@/constants/testIds";

/** First 2 initials (or 2 letters) of a dish name — the no-photo fallback. */
export const dishInitials = (name) => {
  const words = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return "??";
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
};

/**
 * The square next to a dish.
 *
 * Most dishes have no photo and will not for a long time, so the initials tile is
 * the normal state, not an error state. A photo simply covers the same square —
 * identical size and rounding, so the row never reflows — and if the file has gone
 * missing the tile comes straight back rather than leaving a broken image.
 */
const DishThumb = ({ dishId, name, imageUrl }) => {
  const [failed, setFailed] = useState(false);
  const src = failed ? null : mediaUrl(imageUrl);

  return (
    <div
      data-photo-placeholder={src ? undefined : "true"}
      data-testid={src ? undefined : PHOTO.thumbInitials(dishId)}
      className="w-14 h-14 sm:w-16 sm:h-16 rounded-xl bg-neutral-900 text-white flex items-center justify-center relative overflow-hidden flex-shrink-0"
    >
      {src ? (
        <img
          data-testid={PHOTO.thumbImage(dishId)}
          src={src}
          alt={name}
          loading="lazy"
          onError={() => setFailed(true)}
          className="absolute inset-0 w-full h-full object-cover"
        />
      ) : (
        <>
          <div
            className="absolute inset-0 opacity-[0.12] pointer-events-none"
            style={{ backgroundImage: "radial-gradient(rgba(255,255,255,1) 1px, transparent 1px)", backgroundSize: "3px 3px" }}
          />
          <span className="relative text-lg font-black tracking-tight">{dishInitials(name)}</span>
        </>
      )}
    </div>
  );
};

const DishRow = ({ dish, qtyInCart, onAdd, onDec, onOpenPhoto }) => {
  const { t } = useTranslation();
  // Macros keep their notation in every language — it is the same shorthand the
  // menu and the AI plates use (see lib/format.js).
  const macros = `${dish.p}P · ${dish.f}F · ${dish.c}C · ${dish.kcal} kcal`;
  const hideMacros = dish.category === "sauces";

  return (
    <div
      data-testid={`dish-row-${dish.id}`}
      className="flex items-center gap-3 py-3.5 border-b border-neutral-100 last:border-b-0"
    >
      <button
        data-testid={PHOTO.thumbButton(dish.id)}
        onClick={() => onOpenPhoto(dish)}
        className="active:scale-95 transition-transform"
        aria-label={t("dish.viewPhoto", { name: dish.name })}
      >
        <DishThumb dishId={dish.id} name={dish.name} imageUrl={dish.image_url} />
      </button>

      <div className="flex-1 min-w-0">
        <div className="font-bold text-[15px] text-neutral-900 leading-tight">{dish.name}</div>
        {(dish.variant || dish.milk) && (
          <div className="text-[11px] text-neutral-500 mt-0.5 leading-snug">
            {dish.variant && (
              <span data-testid={`dish-variants-${dish.id}`}>{dish.variant.join(" / ")}</span>
            )}
            {dish.milk && !dish.variant && (
              <span data-testid={`dish-milk-hint-${dish.id}`}>{t("dish.chooseMilk")}</span>
            )}
          </div>
        )}
        {!hideMacros && (
          <div className="text-[11px] font-mono text-neutral-500 mt-1 tracking-tight">{macros}</div>
        )}
        <div className="text-[15px] font-black text-[#E34A27] mt-1">{formatPrice(dish.price)}</div>
      </div>

      <div className="flex items-center gap-2">
        {qtyInCart > 0 && (
          <>
            <button
              data-testid={`dish-dec-${dish.id}`}
              onClick={() => onDec(dish)}
              className="w-9 h-9 rounded-full border border-neutral-300 text-neutral-700 flex items-center justify-center active:scale-95 transition-transform bg-white"
              aria-label={t("dish.removeOne")}
            >
              <Minus size={16} />
            </button>
            <span data-testid={`dish-qty-${dish.id}`} className="w-5 text-center font-bold text-sm text-neutral-900">
              {qtyInCart}
            </span>
          </>
        )}
        <button
          data-testid={`dish-add-${dish.id}`}
          onClick={() => onAdd(dish)}
          className="w-10 h-10 rounded-full bg-neutral-900 text-white flex items-center justify-center active:scale-95 transition-transform shrink-0"
          aria-label={t("dish.addToOrder")}
        >
          <Plus size={18} />
        </button>
      </div>
    </div>
  );
};

export default DishRow;
